import React from 'react';
import './styles.css';
import { IoSkull } from 'react-icons/io5';

interface CounterControlProps {
    name: string;
    label: string;
    value: number;
    min: number;
    max: number;
    onChange: (name: string, value: number) => void;
    showThermometer?: boolean;
}

// 10-step classic thermometer palette: blue, light blue, cyan, green, yellow-green, yellow, orange, red-orange, red, dark red
const THERMOMETER_COLORS = [
    '#2196f3', // 0 Blue
    '#4fc3f7', // 1 Light Blue
    '#00bcd4', // 2 Cyan
    '#4caf50', // 3 Green
    '#8bc34a', // 4 Yellow-Green
    '#ffeb3b', // 5 Yellow
    '#ffc107', // 6 Orange-Yellow
    '#ff9800', // 7 Orange
    '#ff5722', // 8 Red-Orange
    '#d32f2f', // 9 Red
];

const getThermometerColor = (percent: number) => {
    const idx = Math.min(
        THERMOMETER_COLORS.length - 1,
        Math.floor(percent * THERMOMETER_COLORS.length)
    );
    return THERMOMETER_COLORS[idx];
};

const SkullIcon = () => (
    <span
        role="img"
        aria-label="skull"
        style={{
            fontSize: 18,
            display: 'inline-flex',
            alignItems: 'center',
            color: '#d32f2f',
            marginTop: 1,
        }}
    >
        <IoSkull style={{color: "var(--color-white)"}} />
    </span>
);

const CounterControl: React.FC<CounterControlProps> = ({
    name,
    label,
    value,
    min,
    max,
    onChange,
    showThermometer = false,
}) => {
    const handleDecrement = () => {
        if (value > min) onChange(name, value - 1);
    };
    const handleIncrement = () => {
        if (value < max) onChange(name, value + 1);
    };

    const percent = (value - min) / (max - min);

    return (
        <div className="counter-control">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button type="button" className='button button-step' onClick={handleDecrement} disabled={value <= min}>-</button>
                <div>
                    <span
                        className="counter"
                        style={{
                            ...(showThermometer && percent > 0.85
                                ? {
                                    animation: 'vibe 0.3s infinite alternate',
                                    color: '#d32f2f',
                                }
                                : {}),
                        }}
                    >
                        {value >= max && showThermometer ? <SkullIcon /> : value}
                    </span>
                </div>
                <button type="button" className='button button-step' onClick={handleIncrement} disabled={value >= max}>+</button>
            </div>
            <label htmlFor={name}>{label}</label>
            {showThermometer && (
                <div className="thermometer">
                    <div
                        style={{
                            height: '100%',
                            width: `${percent * 100}%`,
                            borderRadius: 6,
                            background: getThermometerColor(percent),
                            transition: 'width 0.2s, background 0.2s',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            ...(percent > 0.85
                                ? { animation: 'vibe 0.3s infinite alternate' }
                                : {}),
                        }}
                    />
                </div>
            )}
            {/* Keyframes for vibing animation */}
            <style>
                {`
                @keyframes vibe {
                    0% { transform: scale(1) rotate(-2deg);}
                    100% { transform: scale(1.08) rotate(2deg);}
                }
                `}
            </style>
        </div>
    );
};

export default CounterControl;